import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useApp } from '@/context/AppContext';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import {
  PRESETS, COLOR_FIELDS, DEFAULT_UI, DEFAULT_BRANDING, presetById, defaultThemeConfig,
  hexToHslTriple, hslTripleToHex, contrastRatio, applyThemeConfig, normaliseTheme,
} from '@/lib/theme';
import { Palette, Check, RotateCcw, Save, Eye, Upload, AlertTriangle, Paintbrush2, Trash2 } from 'lucide-react';

const UI_OPTIONS = [
  { key: 'cardStyle', label: 'Card style', choices: [['standard', 'Standard'], ['rounded', 'Rounded'], ['sharp', 'Sharp']] },
  { key: 'buttonStyle', label: 'Button style', choices: [['standard', 'Standard'], ['rounded', 'Rounded'], ['pill', 'Pill']] },
  { key: 'density', label: 'Density', choices: [['comfortable', 'Comfortable'], ['compact', 'Compact']] },
  { key: 'borderIntensity', label: 'Border intensity', choices: [['subtle', 'Subtle'], ['normal', 'Normal']] },
  { key: 'sidebar', label: 'Sidebar', choices: [['normal', 'Normal'], ['compact', 'Compact']] },
];

// One small preview swatch surface using inline styles from the *editing* tokens
// so the owner sees changes instantly without touching the global theme.
function Preview({ tokens, ui }) {
  const c = (k) => `hsl(${tokens[k]})`;
  const fg = c('foreground');
  const radius = ui.cardStyle === 'sharp' ? 0 : ui.cardStyle === 'standard' ? 8 : 16;
  const btnRadius = ui.buttonStyle === 'pill' ? 9999 : ui.buttonStyle === 'standard' ? 8 : 12;
  const shadow = ui.shadows ? '0 4px 14px rgba(0,0,0,0.18)' : 'none';
  const btnFg = tokens.buttonForeground ? `hsl(${tokens.buttonForeground})` : (contrastRatio(tokens.button, '0 0% 100%') >= contrastRatio(tokens.button, '222 15% 12%') ? '#fff' : '#1a1d24');
  const badge = (bg, label) => (
    <span style={{ background: c(bg), color: '#fff', borderRadius: 9999, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>{label}</span>
  );
  return (
    <div data-testid="appearance-preview" style={{ background: c('background'), borderRadius: radius, overflow: 'hidden', border: `1px solid hsl(${tokens.border})` }}>
      <div style={{ display: 'flex', minHeight: 220 }}>
        {/* Sidebar */}
        <div style={{ width: 92, background: c('sidebar'), padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ height: 8, width: '70%', background: c('primary'), borderRadius: 4 }} />
          <div style={{ height: 6, width: '85%', background: 'rgba(255,255,255,0.25)', borderRadius: 4, marginTop: 6 }} />
          <div style={{ height: 6, width: '65%', background: 'rgba(255,255,255,0.18)', borderRadius: 4 }} />
          <div style={{ height: 6, width: '75%', background: 'rgba(255,255,255,0.18)', borderRadius: 4 }} />
        </div>
        {/* Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header bar */}
          <div style={{ background: c('header'), color: fg, borderBottom: `1px solid hsl(${tokens.border})`, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, fontWeight: 700 }}>Dashboard</span>
            <span style={{ fontSize: 10, color: c('mutedForeground') }}>Head Office</span>
          </div>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* cards */}
          <div style={{ display: 'flex', gap: 8 }}>
            {['Sales', 'Stock'].map((t) => (
              <div key={t} style={{ flex: 1, background: c('card'), color: fg, borderRadius: radius, boxShadow: shadow, border: `1px solid hsl(${tokens.border})`, padding: 10 }}>
                <div style={{ color: c('mutedForeground'), fontSize: 10 }}>{t}</div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>₹ 1,240</div>
              </div>
            ))}
          </div>
          {/* input + button */}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, background: c('card'), color: fg, border: `1px solid hsl(${tokens.border})`, borderRadius: btnRadius, padding: '8px 10px', fontSize: 12 }}>Barcode…</div>
            <button style={{ background: c('button'), color: btnFg, border: 'none', borderRadius: btnRadius, padding: '8px 14px', fontSize: 12, fontWeight: 600, boxShadow: shadow }}>Add</button>
          </div>
          {/* badges */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {badge('success', 'Paid')}{badge('warning', 'Pending')}{badge('destructive', 'Cancelled')}
            <span style={{ background: c('accent'), color: fg, borderRadius: 9999, padding: '2px 8px', fontSize: 10 }}>AVAILABLE</span>
          </div>
          {/* invoice snippet */}
          <div style={{ background: c('card'), color: fg, borderRadius: radius, border: `1px solid hsl(${tokens.border})`, padding: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}><span style={{ color: c('mutedForeground') }}>Total</span><span style={{ fontWeight: 700, color: c('primary') }}>₹ 3,900</span></div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Single colour row: swatch + native picker + hex field.
function ColorRow({ field, value, onChange }) {
  const hex = hslTripleToHex(value);
  const [text, setText] = useState(hex);
  useEffect(() => { setText(hslTripleToHex(value)); }, [value]);
  const commit = (v) => {
    const triple = hexToHslTriple(v);
    if (triple) onChange(triple);
  };
  return (
    <div className="flex items-center gap-2" data-testid={`appearance-color-${field.key}`}>
      <label className="relative h-9 w-9 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-border" style={{ background: `hsl(${value})` }}>
        <input type="color" value={hex} onChange={(e) => commit(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0"
          data-testid={`appearance-colorpicker-${field.key}`} aria-label={field.label} />
      </label>
      <div className="min-w-0 flex-1">
        <Label className="text-[11px] text-muted-foreground">{field.label}</Label>
        <Input value={text} onChange={(e) => setText(e.target.value)} onBlur={() => commit(text)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(text); }}
          className="mt-0.5 h-9 rounded-lg font-mono text-xs uppercase" data-testid={`appearance-hex-${field.key}`} />
      </div>
    </div>
  );
}

export function AppearancePanel() {
  const { settings, loadSettings, user } = useApp();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;

  const initial = useMemo(() => normaliseTheme(settings?.theme) || defaultThemeConfig(), [settings]);
  const withHeader = (tk) => ({ ...tk, header: tk.header || tk.card });
  const [tokens, setTokens] = useState(withHeader(initial.tokens));
  const [ui, setUi] = useState({ ...DEFAULT_UI, ...initial.ui });
  const [preset, setPreset] = useState(initial.preset || 'custom');
  const [branding, setBranding] = useState({ ...DEFAULT_BRANDING, ...(settings?.branding || {}) });
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync editor when settings first arrive.
  useEffect(() => {
    const n = normaliseTheme(settings?.theme) || defaultThemeConfig();
    setTokens(withHeader(n.tokens)); setUi({ ...DEFAULT_UI, ...n.ui }); setPreset(n.preset || 'custom');
    setBranding({ ...DEFAULT_BRANDING, ...(settings?.branding || {}) });
  }, [settings]);

  const setToken = useCallback((key, triple) => {
    setTokens((t) => ({ ...t, [key]: triple }));
    setPreset('custom');
  }, []);

  const choosePreset = (p) => {
    setTokens({ ...p.tokens }); setPreset(p.id); setAck(false);
    toast.message(`${p.name} selected`, { description: 'Tap "Apply" to preview it live, or "Save" to make it permanent.' });
  };

  // ---- Contrast checks (WCAG) --------------------------------------------
  const warnings = useMemo(() => {
    const w = [];
    const pairs = [
      ['Text on background', tokens.foreground, tokens.background],
      ['Text on card', tokens.foreground, tokens.card],
      ['Muted text on card', tokens.mutedForeground, tokens.card],
    ];
    pairs.forEach(([label, a, b]) => {
      const r = contrastRatio(a, b);
      if (r < 3) w.push({ label, ratio: r, severity: 'critical' });
      else if (r < 4.5) w.push({ label, ratio: r, severity: 'warn' });
    });
    return w;
  }, [tokens]);
  const hasCritical = warnings.some((x) => x.severity === 'critical');

  const apply = () => { applyThemeConfig({ tokens, ui }); toast.success('Theme applied for preview'); };

  const revert = () => {
    const n = normaliseTheme(settings?.theme) || defaultThemeConfig();
    setTokens(withHeader(n.tokens)); setUi({ ...DEFAULT_UI, ...n.ui }); setPreset(n.preset || 'custom');
    setBranding({ ...DEFAULT_BRANDING, ...(settings?.branding || {}) });
    applyThemeConfig(n); setAck(false);
    toast.message('Reverted to your saved theme');
  };

  const resetDefault = () => {
    const d = defaultThemeConfig();
    setTokens(d.tokens); setUi({ ...DEFAULT_UI, ...d.ui }); setPreset(d.preset); setAck(false);
    applyThemeConfig(d);
    toast.message('Reset to SHABZ default (Black + Gold)', { description: 'Tap "Save" to keep it.' });
  };

  const save = async () => {
    if (hasCritical && !ack) {
      toast.error('Low contrast detected. Tick the acknowledge box to save anyway.');
      return;
    }
    setSaving(true);
    try {
      const themePayload = { name: preset === 'custom' ? 'Custom' : presetById(preset).name, preset, tokens, ui, primary: tokens.primary };
      await api.put('/settings', { theme: themePayload, branding });
      await loadSettings();
      applyThemeConfig({ tokens, ui });
      toast.success('Theme saved — applied everywhere');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const uploadLogo = (file) => {
    if (!file) return;
    const okTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!okTypes.includes(file.type)) { toast.error('Please upload a PNG, JPG or SVG image.'); return; }
    if (file.size > 600000) { toast.error('Logo too large (max ~600KB). Use a smaller image.'); return; }
    const reader = new FileReader();
    reader.onload = () => setBranding((b) => ({ ...b, logo: reader.result }));
    reader.onerror = () => toast.error('Could not read that image. Try another file.');
    reader.readAsDataURL(file);
  };

  if (!isOwner) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sc-card" data-testid="appearance-panel">
      <div className="mb-1 flex items-center gap-2"><Palette className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">Theme &amp; Branding</p>
        <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-foreground">Owner only</span></div>
      <p className="mb-4 text-xs text-muted-foreground">Customise your logo and the entire Billing look. Changes apply to every user once saved. This is presentation only — it never affects sales, stock, invoices or data.</p>

      {/* Presets */}
      <p className="mb-2 text-xs font-semibold">Preset themes</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="appearance-presets">
        {PRESETS.map((p) => {
          const active = preset === p.id;
          return (
            <button key={p.id} data-testid={`appearance-preset-${p.id}`} onClick={() => choosePreset(p)}
              className={`flex items-center gap-2 rounded-xl border p-2 text-left transition-colors ${active ? 'border-primary ring-2 ring-primary/40' : 'border-border hover:border-primary/50'}`}>
              <span className="flex shrink-0 gap-0.5">
                <span className="h-8 w-3 rounded-l" style={{ background: `hsl(${p.tokens.background})` }} />
                <span className="h-8 w-3" style={{ background: `hsl(${p.tokens.primary})` }} />
                <span className="h-8 w-3 rounded-r" style={{ background: `hsl(${p.tokens.sidebar})` }} />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-xs font-semibold">{p.name}</span>
                <span className="block truncate text-[10px] text-muted-foreground">{p.desc}</span>
              </span>
              {active && <Check className="ml-auto h-4 w-4 shrink-0 text-primary" />}
            </button>
          );
        })}
        <div className={`flex items-center gap-2 rounded-xl border p-2 ${preset === 'custom' ? 'border-primary ring-2 ring-primary/40' : 'border-border'}`} data-testid="appearance-preset-custom">
          <Paintbrush2 className="h-5 w-5 text-muted-foreground" />
          <span className="text-xs font-semibold">Custom</span>
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-4 flex items-center gap-2"><Eye className="h-3.5 w-3.5 text-muted-foreground" /><p className="text-xs font-semibold">Live preview</p></div>
      <div className="mt-2"><Preview tokens={tokens} ui={ui} /></div>

      {/* Colours */}
      <p className="mb-2 mt-4 text-xs font-semibold">Colours</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {COLOR_FIELDS.map((f) => (
          <ColorRow key={f.key} field={f} value={tokens[f.key] || '0 0% 50%'} onChange={(v) => setToken(f.key, v)} />
        ))}
      </div>

      {/* Contrast warnings */}
      {warnings.length > 0 && (
        <div className={`mt-3 rounded-xl border p-3 text-xs ${hasCritical ? 'border-destructive/40 bg-destructive/10 text-destructive' : 'border-warning/40 bg-warning/10 text-warning'}`} data-testid="appearance-contrast-warning">
          <div className="mb-1 flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Readability check</div>
          <ul className="space-y-0.5">
            {warnings.map((w, i) => (
              <li key={i}>{w.label}: contrast {w.ratio}:1 {w.severity === 'critical' ? '— too low, hard to read' : '— below recommended 4.5:1'}</li>
            ))}
          </ul>
          {hasCritical && (
            <label className="mt-2 flex items-center gap-2 font-medium" data-testid="appearance-ack">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="h-4 w-4" />
              I understand this may be hard to read — save anyway
            </label>
          )}
        </div>
      )}

      {/* UI options */}
      <p className="mb-2 mt-4 text-xs font-semibold">Layout &amp; style</p>
      <div className="space-y-3">
        {UI_OPTIONS.map((opt) => (
          <div key={opt.key} className="flex flex-wrap items-center gap-2">
            <span className="w-28 text-xs text-muted-foreground">{opt.label}</span>
            <div className="flex flex-wrap gap-1.5">
              {opt.choices.map(([val, lbl]) => (
                <button key={val} data-testid={`appearance-ui-${opt.key}-${val}`} onClick={() => setUi((u) => ({ ...u, [opt.key]: val }))}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium ${ui[opt.key] === val ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{lbl}</button>
              ))}
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Subtle shadows</span>
          <Switch checked={ui.shadows} onCheckedChange={(v) => setUi((u) => ({ ...u, shadows: v }))} data-testid="appearance-ui-shadows" />
        </div>
      </div>

      {/* Branding */}
      <p className="mb-2 mt-4 text-xs font-semibold">Branding</p>
      <div className="rounded-xl border border-border p-3">
        <div className="flex items-center gap-3">
          {branding.logo ? (
            <img src={branding.logo} alt="Logo" className="h-12 w-12 rounded-lg object-contain ring-1 ring-border" data-testid="appearance-logo-preview" />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary text-xs text-muted-foreground">Logo</div>
          )}
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium">
            <Upload className="h-3.5 w-3.5" /> {branding.logo ? 'Change logo' : 'Upload logo'}
            <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="appearance-logo-upload" />
          </label>
          {branding.logo && (
            <button onClick={() => setBranding((b) => ({ ...b, logo: '' }))} className="inline-flex items-center gap-1 text-xs text-destructive" data-testid="appearance-logo-remove"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="w-28 text-xs text-muted-foreground">Logo size</span>
          <div className="flex gap-1.5">
            {[['small', 'Small'], ['medium', 'Medium'], ['large', 'Large']].map(([v, l]) => (
              <button key={v} data-testid={`appearance-logo-size-${v}`} onClick={() => setBranding((b) => ({ ...b, logoSize: v }))}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium ${branding.logoSize === v ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Show logo in sidebar</span>
          <Switch checked={branding.showInSidebar} onCheckedChange={(v) => setBranding((b) => ({ ...b, showInSidebar: v }))} data-testid="appearance-toggle-sidebar-logo" />
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Show logo on invoices / receipts</span>
          <Switch checked={branding.showOnInvoice} onCheckedChange={(v) => setBranding((b) => ({ ...b, showOnInvoice: v }))} data-testid="appearance-toggle-invoice-logo" />
        </div>
      </div>

      {/* Actions */}
      <div className="sticky bottom-0 z-10 mt-4 grid grid-cols-2 gap-2 border-t border-border bg-card pt-3 sm:grid-cols-4">
        <Button variant="secondary" className="h-11 rounded-xl" onClick={apply} data-testid="appearance-apply"><Eye className="mr-1 h-4 w-4" />Apply</Button>
        <Button className="h-11 rounded-xl" onClick={save} disabled={saving} data-testid="appearance-save"><Save className="mr-1 h-4 w-4" />{saving ? 'Saving…' : 'Save Theme'}</Button>
        <Button variant="secondary" className="h-11 rounded-xl" onClick={resetDefault} data-testid="appearance-reset"><RotateCcw className="mr-1 h-4 w-4" />SHABZ Default</Button>
        <Button variant="outline" className="h-11 rounded-xl" onClick={revert} data-testid="appearance-cancel">Cancel</Button>
      </div>
    </div>
  );
}
